import { MouseEvent, ReactNode, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Box } from '../UI/Box'

import { Dialog, DialogContent } from '../Digalog'
import { Toaster } from '../../App'

const Inner = styled(Box)<{
  scrollAmount?: number
  overflowing?: boolean
  minWidth?: number
  minHeight?: number
  isClipped?: boolean
}>`
  align-items: flex-start;
  ${({ isClipped }) => (isClipped ? 'padding: 2rem;' : '')};
  min-width: ${({ minWidth }) => `${minWidth}px` || '470px'};
  max-width: min(900px, 80vw);
  min-height: ${({ minHeight }) => `${minHeight}px` || '350px'};
  max-height: 100vh;
  margin: 0 auto;
  z-index: 100;
  overflow: scroll;

  // hide scrollbar
  &::-webkit-scrollbar {
    display: none;
  }

  position: relative;
  z-index: 1;
  border-radius: var(--units-8, 8px);

  border: var(--units-1, 1px) solid var(--stroke-secondary, #333);

  background: var(--surface-primary, #080808);

  @media screen and (max-width: 1000px) {
    ${({ isClipped }) => (isClipped ? 'padding: 1rem 1.5rem;' : '')};
    min-width: 470px;
    max-width: min(900px, 90vw);
    margin-bottom: 2rem; // just to give a little bit of space at the bottom
  }
  @media screen and (max-width: 768px) {
    min-width: 300px;
  }
`

const Container = styled.div`
  position: relative;
  > img {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: 0;
    right: 0;
    margin: 0 auto;

    @media screen and (max-width: 1100px) {
      display: none;
    }
  }

  @media screen and (max-width: 768px) {
    position: absolute;
    top: 120px;
    left: 0;
    right: 0;
  }
`

export const ModalTitle = styled.h4`
  color: #fff;
  text-align: center;
  font-feature-settings: 'ss01' on;
  font-family: 'Space Grotesk';
  font-size: 48px;
  font-style: normal;
  font-weight: 700;
  line-height: 50px; /* 104.167% */
  margin: 0 auto;

  @media screen and (max-width: 768px) {
    font-size: 26px;
    line-height: 1.2;

    // this margin is for spacing the back button on missions modal
    margin-top: 40px;
  }
`

export const ModalText = styled.span`
  color: #fff;
  text-align: center;
  font-feature-settings: 'dlig' on, 'ss01' on;
  font-family: 'Space Grotesk';
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 28px; /* 140% */
  letter-spacing: -0.8px;

  @media screen and (max-width: 768px) {
    font-size: 16px;
    line-height: 1;
    letter-spacing: normal;
  }
`

export const ModalErrorMessage = styled.span`
  color: #ff623e;
  text-align: center;
  font-family: Space Grotesk;
  font-size: 16px;
  font-style: normal;
  font-weight: 300;
  line-height: normal;
  max-width: 300px;
`

export type DivOnClickEvent =
  | MouseEvent<HTMLDivElement, globalThis.MouseEvent>
  | MouseEvent<HTMLDivElement, MouseEvent>

export function stopPropagation(e: DivOnClickEvent) {
  e.stopPropagation()
}

const EmbeddableModal = ({
  children,
  dismiss,
  isVisible,
  hideBolt,
  minWidth,
  minHeight,
  isClipped = true,
}: {
  children: ReactNode
  dismiss: () => void
  isVisible: boolean
  hideBolt?: boolean
  minWidth?: number
  minHeight?: number
  isClipped?: boolean
}) => {
  const [scrollAmount, setScrollAmount] = useState(0)
  const innerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!innerRef.current) return
    let refClone = innerRef.current

    function handleScroll() {
      if (refClone) {
        setScrollAmount(refClone.scrollTop)
      }
    }
    if (refClone) {
      refClone.addEventListener('scroll', handleScroll)
    }
    return () => {
      if (refClone) {
        refClone.removeEventListener('scroll', handleScroll)
      }
    }
  }, [innerRef])

  return (
    // https://floating-ui.com/docs/dialog#reusable-dialog-component
    // Check the above to understand if you need to extend it
    <Dialog onOpenChange={dismiss} open={isVisible}>
      <DialogContent>
        <Container>
          <Inner
            ref={innerRef}
            overflowing={scrollAmount > 0}
            minHeight={minHeight}
            minWidth={minWidth}
            isClipped={isClipped}
          >
            {children}
          </Inner>
          <Toaster />
        </Container>
      </DialogContent>
    </Dialog>
  )
}

export default EmbeddableModal
